import React, { useMemo, useRef } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import PDPVideoCarousel from '~/builder/components/PDPVideoCarousel';
import BaseProductDetailPrice from '~/pdp/components/ProductDetailPrice';
import useLoyaltyPricing from '~/pdp/utils/useLoyaltyPricing';
import useProfileSizes from '~/pdp/utils/useProfileSizes';
import featureNames from '~/shared/constants/featureFlags';
import { LoyaltyTiers } from '~/shared/constants/loyaltyTiers';
import { ProductTag, ProductFPL } from '~/shared/constants/products';
import { getBrandValue, isSavage } from '~/shared/utils/brandNameHelper';
import getCartPrice from '~/shared/utils/getCartPrice';
import { isLockedToMember } from '~/shared/utils/loyalty';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useLoyalty } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  mobile,
  Separator,
  ErrorContext,
  ProductName as BaseProductName,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { ProductType } from '~/techstyle-shared/react-products';
import { useDomain, useSession } from '~/techstyle-shared/redux-core';

import GroupSizeSelectorProvider from '../GroupSizeSelectorProvider';
import ModelImageProviderWrapper from '../ModelImageProviderWrapper';
import PDPHeaderContent from '../PDPHeaderContent';
import PlatinumUnlockedMessage from '../PlatinumUnlockedMessage';
import ProductCTA from '../ProductCTA';
import ProductDetailEmbroideryProvider from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import ProductDetailImages from '../ProductDetailImages';
import ProductDetailImagesGrid from '../ProductDetailImagesGrid';
import ProductDetails from '../ProductDetails';
import ProductDetailsSection from '../ProductDetailsSection';
import ProductDetailUGC from '../ProductDetailUGC';

const ProductDetailPrice = styled(BaseProductDetailPrice)`
  margin-top: 16px;
  ${mobile`
    margin-top: 0;
  `}
`;

const ProductDetailsSectionWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 36px;

  button {
    cursor: pointer;
  }

  ${mobile`
    margin-bottom: 0;
  `}
`;

const MainProductArea = styled.div`
  margin-bottom: 32px;
  display: grid;
  grid-template-columns: fit-content(320px) minmax(0, 1fr);
  grid-template-areas:
    'mobileHeaderArea mobileHeaderArea'
    'productImageArea productDetailArea';

  ${mobile`
    flex-direction: column;
    grid-template-columns: 1fr;
    grid-template-areas: "mobileHeaderArea"
                         "productImageArea"
                         "productDetailArea";
  `}

  grid-template-columns: 1fr 424px;
  column-gap: 32px;

  @media (min-width: ${({ theme }) =>
    theme.breakpoints.tablet.minWidth}px) and (max-width: ${({ theme }) =>
  theme.breakpoints.tablet.maxWidth}px) {
    grid-template-columns: 1fr 340px;
  }
}
`;

const ProductDetailWrapper = styled.div`
  grid-area: productDetailArea;
  display: flex;
  flex-direction: column;

  ${mobile`
    max-width: 100vw;
    margin-left: 0;
  `}
`;

const ProductImageArea = styled.div`
  grid-area: productImageArea;
`;

const PriceWrapper = styled.div`
  ${mobile`
    padding: 16px;
  `};
`;

const separatorStyle = css`
  color: ${({ theme }) => theme.colors.separator};
  margin-block: 14px 16px;
`;

const ProductName = styled(BaseProductName)`
  display: inline;
  color: ${({ theme }) => theme.colors.textDefault};
  margin-right: 6px;

  ${mobile`
    margin-bottom: 0;
    margin-right: 0;
  `}

  ${({ theme }) => theme.pdpMainContent.productName};
`;

const PDPMainContent = ({
  product,
  relatedProductsLoading,
  isVip,
  builderQueries,
}) => {
  const { isLoggedIn } = useSession();
  const { tld } = useDomain();
  const isUSDomain = tld === '.com';
  const isCADomain = tld === '.ca';
  const { isMobile } = useBreakpoint();
  const { tier } = useLoyalty();
  const reviewSectionRef = useRef();
  const productRef = useRef();
  const { isTokenOnly } = useEMPPricing(product);
  const { isLoyaltyItem } = useLoyaltyPricing(product);
  const secondarySite = useSecondarySite();
  // auto filter defaults to disabled

  const { enabled: initialIsAfterpayEnabled } = useHasFeature(
    featureNames.AFTERPAY
  );
  const { enabled: showVideoCarousel } = useHasFeature(
    featureNames.PDP_VIDEO_CAROUSEL
  );
  const { data: additionalPaymentMethods } = useHasFeature(
    featureNames.ADDITIONAL_PAYMENT_METHODS
  );
  const isAfterpayEnabled = useMemo(() => {
    if (isSavage() && additionalPaymentMethods) {
      return ['afterpay', 'both'].includes(additionalPaymentMethods);
    }

    return initialIsAfterpayEnabled;
  }, [additionalPaymentMethods, initialIsAfterpayEnabled]);
  const { enabled: initialIsKlarnaEnabled } = useHasFeature(
    featureNames.KLARNA
  );
  const isKlarnaEnabled = useMemo(() => {
    if (isSavage() && additionalPaymentMethods) {
      return ['klarna', 'both'].includes(additionalPaymentMethods);
    }

    return initialIsKlarnaEnabled;
  }, [additionalPaymentMethods, initialIsKlarnaEnabled]);
  const { isVipCart } = useCart({ isLoginRequired: true }) || {};

  const curalateContainerId = config.get('public.curalate.curalateContainerId');
  const showUgcContent = getBrandValue({
    savage: false,
    default: isUSDomain && curalateContainerId,
  });

  const showEnhancedLayout = config.get(
    'public.brand.showEnhancedProductLayout'
  );

  const showProductRating = product?.reviewCount > 0;
  const showProductDetailsProductRating =
    isMobile && showEnhancedLayout ? false : showProductRating;

  const isBundle = product?.productTypeId === ProductType.BUNDLE;

  const isPlatinumExclusive = product?.tagIdList.includes(
    ProductTag.platinumExclusive
  );
  const isLocked = isLockedToMember(tier?.label, LoyaltyTiers.PLATINUM);

  const showAfterPay = !isTokenOnly && !isLoyaltyItem && isAfterpayEnabled;
  const showKlarna = !isTokenOnly && !isLoyaltyItem && isKlarnaEnabled;

  const { profileSizes } = useProfileSizes();

  const featuredProductLocationIdList =
    product?.featuredProductLocationIdList || [];

  const showWearItWith =
    featuredProductLocationIdList.includes(ProductFPL.completeTheLookOutfit) &&
    isLoggedIn &&
    isVip &&
    (isUSDomain || isCADomain);

  const wearItWithProductIds = product?.wearItWithProductIds || [];

  const { masterProductId, productLabel } = product;

  const productPrice = getCartPrice({
    product,
    isVip,
    isVipCart,
    secondarySite,
  });
  let ProductImages = ProductDetailImages;

  if (!isMobile) {
    ProductImages = ProductDetailImagesGrid;
  }

  const productName = (
    <ProductName
      data-autotag="pdp-main-product-name"
      forwardedAs="h1"
      tabIndex="0"
    />
  );

  return (
    <ModelImageProviderWrapper profileSizes={profileSizes}>
      <GroupSizeSelectorProvider>
        <MainProductArea>
          {isMobile && (
            <PDPHeaderContent
              isLocked={isLocked}
              isPlatinumExclusive={isPlatinumExclusive}
              product={product}
              productName={productName}
              productRef={productRef}
              reviewSectionRef={reviewSectionRef}
              showProductRating={showProductRating}
            />
          )}
          <ProductImageArea>
            <ProductImages />
          </ProductImageArea>
          <ProductDetailWrapper
            data-cnstrc-product-detail
            data-cnstrc-item-id={masterProductId}
            data-cnstrc-item-name={productLabel}
            data-cnstrc-item-price={productPrice}
          >
            {!isMobile && (
              <PDPHeaderContent
                isLocked={isLocked}
                isPlatinumExclusive={isPlatinumExclusive}
                product={product}
                productName={productName}
                productRef={productRef}
                reviewSectionRef={reviewSectionRef}
                showProductRating={showProductRating}
              />
            )}
            {!isMobile && (
              <>
                <PriceWrapper>
                  <ProductDetailPrice />
                </PriceWrapper>
                <Separator thickness={1} separatorStyle={separatorStyle} />
                {isPlatinumExclusive && !isLocked && (
                  <PlatinumUnlockedMessage />
                )}
              </>
            )}
            <ErrorContext>
              <ProductDetailEmbroideryProvider>
                <ProductDetails
                  productRef={productRef}
                  reviewSectionRef={reviewSectionRef}
                  showProductRating={showProductDetailsProductRating}
                  isAfterpayEnabled={isAfterpayEnabled}
                  relatedProductsLoading={relatedProductsLoading}
                />
                <ProductCTA
                  isLocked={isLocked}
                  isPlatinumExclusive={isPlatinumExclusive}
                  showWearItWith={showWearItWith}
                  tier={tier}
                  wearItWithProductIds={wearItWithProductIds}
                  isAfterpayEnabled={showAfterPay}
                  isKlarnaEnabled={showKlarna}
                  isPDP
                />
                {showVideoCarousel && (
                  <PDPVideoCarousel
                    product={product}
                    builderQueries={builderQueries}
                    isThumbnailsMode
                  />
                )}
                {!isBundle && (
                  <ProductDetailsSectionWrapper>
                    <ProductDetailsSection />
                  </ProductDetailsSectionWrapper>
                )}
              </ProductDetailEmbroideryProvider>
            </ErrorContext>
            {showUgcContent ? (
              <>
                <Separator thickness={2} separatorStyle={separatorStyle} />
                <ProductDetailUGC experienceName={curalateContainerId} />
              </>
            ) : null}
          </ProductDetailWrapper>
        </MainProductArea>
      </GroupSizeSelectorProvider>
    </ModelImageProviderWrapper>
  );
};

PDPMainContent.propTypes = {
  builderQueries: PropTypes.object,
  isVip: PropTypes.bool,
  product: PropTypes.object,
  relatedProductsLoading: PropTypes.bool,
};

export default PDPMainContent;
